import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthOrSession } from '../lib/authOrSession';
import { de } from '../lib/i18n/de';
import { Button } from '../components/ui/Button';
import { emailSchema, firstIssueMessage, passwordPairSchema } from '../lib/authValidation';

interface LocationState {
  from?: string;
}

type LoginMethod = 'magic' | 'password';
type PasswordView = 'login' | 'signup' | 'forgot';

/**
 * Login-Seite im Editorial-Stil.
 *
 * Zwei Anmeldemethoden über einen segmentierten Umschalter:
 *   - „Anmelde-Link“ (Default, primär für Mandanten): E-Mail eingeben →
 *     Bestätigungs-Screen mit OTP-Fallback. In Dev simulieren wir den
 *     Link-Klick über einen Button (Mock).
 *   - „Passwort“: Login mit E-Mail + Passwort, plus „Konto mit Passwort
 *     erstellen“ (Signup mit Bestätigungs-Mail) und „Passwort vergessen
 *     oder noch keines festgelegt?“ (Recovery-Flow → /auth/reset, deckt
 *     auch Bestands-User ab, die bisher nur Magic-Link genutzt haben).
 *
 * Kein Social Login — bewusste DSGVO-Entscheidung (siehe lib/supabase.ts).
 */
export function Login() {
  const {
    user,
    status,
    lastMagicLinkEmail,
    isMock,
    signIn,
    confirmMagicLink,
    verifyOtp,
    signInWithPassword,
    signUpWithPassword,
    requestPasswordReset,
  } = useAuthOrSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [method, setMethod] = useState<LoginMethod>('magic');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // OTP-Fallback: separater State, damit Formular-Errors nicht kollidieren.
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  // Passwort-Flow (Login / Signup / Forgot)
  const [pwView, setPwView] = useState<PasswordView>('login');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [signupDone, setSignupDone] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  if (user) {
    const from = (location.state as LocationState | null)?.from ?? '/cases';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await signIn(email);
      if (!res.ok) {
        setError(res.error ?? 'Anmeldung fehlgeschlagen.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    confirmMagicLink();
    const from = (location.state as LocationState | null)?.from ?? '/cases';
    void navigate(from, { replace: true });
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    setOtpSubmitting(true);
    try {
      const res = await verifyOtp(otp);
      if (!res.ok) {
        setOtpError(res.error ?? de.login.otpFallbackInvalid);
        return;
      }
      // In Production löst onAuthStateChange den Redirect über useSession.
      // Im Mock-Pfad simuliert verifyOtp den Magic-Link-Click — der User-
      // Effect oben (user-Check) navigiert dann beim nächsten Render.
      if (isMock) {
        const from = (location.state as LocationState | null)?.from ?? '/cases';
        void navigate(from, { replace: true });
      }
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : de.login.otpFallbackInvalid);
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    const parsedEmail = emailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setPwError(firstIssueMessage(parsedEmail));
      return;
    }
    if (password.length === 0) {
      setPwError(de.authValidation.passwordTooShort);
      return;
    }
    setPwSubmitting(true);
    try {
      const res = await signInWithPassword(parsedEmail.data, password);
      if (!res.ok) {
        setPwError(res.error ?? 'Anmeldung fehlgeschlagen.');
      }
      // Erfolg: Session ist gesetzt → user-Check oben rendert <Navigate>.
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Unbekannter Fehler.');
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    const parsedEmail = emailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setPwError(firstIssueMessage(parsedEmail));
      return;
    }
    const parsedPair = passwordPairSchema.safeParse({ password, passwordRepeat });
    if (!parsedPair.success) {
      setPwError(firstIssueMessage(parsedPair));
      return;
    }
    setPwSubmitting(true);
    try {
      const res = await signUpWithPassword(parsedEmail.data, password);
      if (!res.ok) {
        setPwError(res.error ?? 'Registrierung fehlgeschlagen.');
        return;
      }
      // Production: Bestätigungs-Mail wurde verschickt → lokaler Erfolgs-
      // Screen. Mock: direkt authentifiziert → user-Check navigiert.
      if (!isMock) {
        setSignupDone(true);
      }
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Unbekannter Fehler.');
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    const parsedEmail = emailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setPwError(firstIssueMessage(parsedEmail));
      return;
    }
    setPwSubmitting(true);
    try {
      const res = await requestPasswordReset(parsedEmail.data);
      if (!res.ok) {
        setPwError(res.error ?? 'Anfrage fehlgeschlagen.');
        return;
      }
      setForgotDone(true);
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Unbekannter Fehler.');
    } finally {
      setPwSubmitting(false);
    }
  };

  const switchPwView = (view: PasswordView) => {
    setPwView(view);
    setPwError(null);
    setPassword('');
    setPasswordRepeat('');
  };

  const [titleLine1, titleLine2] = de.login.title.split('\n');

  return (
    <div className="pb-24 pt-12 md:pt-20">
      {isMock && <DevAuthBanner />}

      {/* Pastell-Akzente */}
      <div className="relative">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-32 w-32 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
        />
        <div
          aria-hidden="true"
          className="absolute right-20 top-24 hidden h-20 w-20 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-private)', opacity: 0.3 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.brand.tagline}
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-7xl">
          <span className="block">{titleLine1}</span>
          <span className="block">{titleLine2}</span>
        </h1>
        <p className="mt-3 font-serif text-base italic text-stone-500 md:mt-4 md:text-lg">
          {de.login.subtitle}
        </p>
      </div>

      <section
        aria-labelledby="login-section"
        className="mt-12 rounded-sm border border-stone-200 bg-white"
      >
        <header className="border-b border-stone-200 px-6 pt-8 pb-6 md:px-10 md:pt-10">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.login.sectionLabel}
          </p>
          <h2
            id="login-section"
            className="mt-2 font-serif text-2xl font-bold uppercase tracking-tight text-stone-900 md:text-3xl"
          >
            {de.login.sectionTitle}
          </h2>
        </header>

        <div className="px-6 py-8 md:px-10 md:py-10">
          {status === 'pending' && lastMagicLinkEmail ? (
            <MagicLinkPendingView
              email={lastMagicLinkEmail}
              isMock={isMock}
              onConfirm={handleConfirm}
              otp={otp}
              otpError={otpError}
              otpSubmitting={otpSubmitting}
              onOtpChange={setOtp}
              onOtpSubmit={handleVerifyOtp}
            />
          ) : signupDone ? (
            <ConfirmationSentView
              title={de.login.signupSuccessTitle}
              body={de.login.signupSuccessBody}
              email={email}
              onBack={() => {
                setSignupDone(false);
                switchPwView('login');
              }}
            />
          ) : forgotDone ? (
            <ConfirmationSentView
              title={de.login.forgotSuccessTitle}
              body={de.login.forgotSuccessBody}
              email={email}
              onBack={() => {
                setForgotDone(false);
                switchPwView('login');
              }}
            />
          ) : (
            <>
              {/* Methoden-Umschalter */}
              <div
                role="group"
                aria-label={de.login.tabAriaLabel}
                className="inline-flex overflow-hidden rounded-sm border border-stone-300"
              >
                <MethodToggleButton
                  active={method === 'magic'}
                  onClick={() => {
                    setMethod('magic');
                    setError(null);
                  }}
                >
                  {de.login.tabMagicLink}
                </MethodToggleButton>
                <MethodToggleButton
                  active={method === 'password'}
                  onClick={() => {
                    setMethod('password');
                    switchPwView('login');
                  }}
                >
                  {de.login.tabPassword}
                </MethodToggleButton>
              </div>

              {method === 'magic' ? (
                <form onSubmit={(e) => void handleSubmit(e)} className="mt-8 space-y-6" noValidate>
                  <div>
                    <EmailField
                      value={email}
                      onChange={setEmail}
                      explainer={de.login.explainer}
                      error={error}
                    />
                  </div>
                  <Button type="submit" disabled={submitting || email.trim().length === 0}>
                    {submitting ? de.login.submitting : `${de.login.submit} →`}
                  </Button>
                </form>
              ) : pwView === 'login' ? (
                <form
                  onSubmit={(e) => void handlePasswordLogin(e)}
                  className="mt-8 space-y-6"
                  noValidate
                >
                  <EmailField
                    value={email}
                    onChange={setEmail}
                    explainer={de.login.passwordExplainer}
                    error={null}
                  />
                  <PasswordField
                    id="login-password"
                    label={de.login.passwordLabel}
                    value={password}
                    onChange={setPassword}
                    autoComplete="current-password"
                    error={pwError}
                  />
                  <div className="flex flex-col gap-4">
                    <Button
                      type="submit"
                      disabled={pwSubmitting || email.trim().length === 0 || password.length === 0}
                    >
                      {pwSubmitting ? de.login.passwordSubmitting : `${de.login.passwordSubmit} →`}
                    </Button>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                      <InlineTextButton
                        onClick={() => {
                          switchPwView('forgot');
                        }}
                      >
                        {de.login.forgotLink}
                      </InlineTextButton>
                      <InlineTextButton
                        onClick={() => {
                          switchPwView('signup');
                        }}
                      >
                        {de.login.signupToggle}
                      </InlineTextButton>
                    </div>
                  </div>
                </form>
              ) : pwView === 'signup' ? (
                <form onSubmit={(e) => void handleSignup(e)} className="mt-8 space-y-6" noValidate>
                  <EmailField
                    value={email}
                    onChange={setEmail}
                    explainer={de.login.signupExplainer}
                    error={null}
                  />
                  <PasswordField
                    id="signup-password"
                    label={de.login.passwordLabel}
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    error={null}
                  />
                  <PasswordField
                    id="signup-password-repeat"
                    label={de.login.signupPasswordRepeatLabel}
                    value={passwordRepeat}
                    onChange={setPasswordRepeat}
                    autoComplete="new-password"
                    error={pwError}
                  />
                  <div className="flex flex-col gap-4">
                    <Button
                      type="submit"
                      disabled={
                        pwSubmitting ||
                        email.trim().length === 0 ||
                        password.length === 0 ||
                        passwordRepeat.length === 0
                      }
                    >
                      {pwSubmitting ? de.login.signupSubmitting : `${de.login.signupSubmit} →`}
                    </Button>
                    <InlineTextButton
                      onClick={() => {
                        switchPwView('login');
                      }}
                    >
                      {de.login.haveAccountToggle}
                    </InlineTextButton>
                  </div>
                </form>
              ) : (
                <form onSubmit={(e) => void handleForgot(e)} className="mt-8 space-y-6" noValidate>
                  <EmailField
                    value={email}
                    onChange={setEmail}
                    explainer={de.login.forgotExplainer}
                    error={pwError}
                  />
                  <div className="flex flex-col gap-4">
                    <Button type="submit" disabled={pwSubmitting || email.trim().length === 0}>
                      {pwSubmitting ? de.login.forgotSubmitting : `${de.login.forgotSubmit} →`}
                    </Button>
                    <InlineTextButton
                      onClick={() => {
                        switchPwView('login');
                      }}
                    >
                      {de.login.backToLogin}
                    </InlineTextButton>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </section>

      {/* Datenschutz-Banner */}
      <aside
        aria-labelledby="privacy-banner"
        className="mt-8 rounded-sm border-l-4 border-stone-900 bg-stone-50 px-6 py-6 md:px-8"
      >
        <p
          id="privacy-banner"
          className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
        >
          {de.login.privacyHeadline}
        </p>
        <p className="mt-2 font-serif text-sm text-stone-700">{de.login.privacyBody}</p>
        <a
          href="/datenschutz"
          className="mt-3 inline-block font-sans text-xs font-medium uppercase tracking-widest text-stone-900 underline hover:no-underline"
        >
          {de.login.privacyLink} →
        </a>
      </aside>

      {/* AVGS-Hinweis */}
      <p className="mt-6 max-w-prose font-serif text-sm italic text-stone-500">
        {de.login.avgsHint}
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Teil-Ansichten & Formular-Bausteine
// -----------------------------------------------------------------------------

interface MagicLinkPendingViewProps {
  readonly email: string;
  readonly isMock: boolean;
  readonly onConfirm: () => void;
  readonly otp: string;
  readonly otpError: string | null;
  readonly otpSubmitting: boolean;
  readonly onOtpChange: (value: string) => void;
  readonly onOtpSubmit: (e: React.FormEvent) => Promise<void>;
}

function MagicLinkPendingView({
  email,
  isMock,
  onConfirm,
  otp,
  otpError,
  otpSubmitting,
  onOtpChange,
  onOtpSubmit,
}: MagicLinkPendingViewProps) {
  return (
    <div className="space-y-4">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.login.successTitle}
      </p>
      <p className="font-serif text-base text-stone-700">
        {de.login.successBody} <span className="font-medium text-stone-900">{email}</span>
      </p>
      {isMock ? (
        <>
          <Button type="button" onClick={onConfirm} className="mt-4">
            {de.login.mockConfirmButton} →
          </Button>
          <p className="font-serif text-xs italic text-stone-500">{de.login.mockConfirmHint}</p>
        </>
      ) : (
        <p className="font-serif text-xs italic text-stone-500">{de.login.successHintProduction}</p>
      )}

      {/* OTP-Fallback: 6-stelliger Code aus derselben Mail, umgeht PKCE.
          Sichtbar nur in Production (im Mock reicht der Demo-Button). */}
      {!isMock && (
        <form
          onSubmit={(e) => void onOtpSubmit(e)}
          className="mt-8 border-t border-stone-200 pt-6"
          noValidate
        >
          <label
            htmlFor="login-otp"
            className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
          >
            {de.login.otpFallbackLabel}
          </label>
          <p className="mt-2 font-serif text-sm italic text-stone-500">
            {de.login.otpFallbackHint}
          </p>
          <input
            id="login-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={otp}
            onChange={(e) => {
              // nur Ziffern, max 6 — verhindert Tippfehler-Pasten
              const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
              onOtpChange(digits);
            }}
            placeholder={de.login.otpFallbackPlaceholder}
            className="mt-4 w-full max-w-[12rem] rounded-sm border border-stone-200 bg-white px-4 py-3 text-center font-mono text-lg tracking-[0.4em] text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          />
          {otpError && (
            <p
              role="alert"
              className="mt-3 font-sans text-xs font-medium uppercase tracking-widest text-rose-600"
            >
              {otpError}
            </p>
          )}
          <Button type="submit" disabled={otpSubmitting || otp.length !== 6} className="mt-4">
            {otpSubmitting ? de.login.otpFallbackSubmitting : `${de.login.otpFallbackSubmit} →`}
          </Button>
        </form>
      )}
    </div>
  );
}

interface ConfirmationSentViewProps {
  readonly title: string;
  readonly body: string;
  readonly email: string;
  readonly onBack: () => void;
}

function ConfirmationSentView({ title, body, email, onBack }: ConfirmationSentViewProps) {
  return (
    <div className="space-y-4">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {title}
      </p>
      <p className="font-serif text-base text-stone-700">
        {body} <span className="font-medium text-stone-900">{email}</span>
      </p>
      <InlineTextButton onClick={onBack}>{de.login.backToLogin}</InlineTextButton>
    </div>
  );
}

interface MethodToggleButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}

function MethodToggleButton({ active, onClick, children }: MethodToggleButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`px-5 py-2.5 font-sans text-xs font-medium uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-900 ${
        active ? 'bg-stone-900 text-white' : 'bg-white text-stone-500 hover:text-stone-900'
      }`}
    >
      {children}
    </button>
  );
}

interface InlineTextButtonProps {
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}

function InlineTextButton({ onClick, children }: InlineTextButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-fit font-sans text-xs font-medium uppercase tracking-widest text-stone-500 underline transition-colors hover:text-stone-900 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
    >
      {children}
    </button>
  );
}

interface EmailFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly explainer: string;
  readonly error: string | null;
}

function EmailField({ value, onChange, explainer, error }: EmailFieldProps) {
  return (
    <div>
      <label
        htmlFor="login-email"
        className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
      >
        {de.login.emailLabel}
      </label>
      <p className="mt-2 font-serif text-sm italic text-stone-500">{explainer}</p>
      <input
        id="login-email"
        type="email"
        required
        autoComplete="email"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={de.login.emailPlaceholder}
        className="mt-4 w-full rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
      />
      {error && (
        <p
          role="alert"
          className="mt-3 font-sans text-xs font-medium uppercase tracking-widest text-rose-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}

interface PasswordFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly autoComplete: 'current-password' | 'new-password';
  readonly error: string | null;
}

function PasswordField({ id, label, value, onChange, autoComplete, error }: PasswordFieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
      >
        {label}
      </label>
      <input
        id={id}
        type="password"
        required
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="mt-4 w-full rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder-stone-300 focus:border-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
      />
      {error && (
        <p
          role="alert"
          className="mt-3 font-sans text-xs font-medium uppercase tracking-widest text-rose-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Dev-Banner: erscheint NUR im Mock-Modus (Vite-Dev und Vitest).
 * Im Production-Build wird `isMock` immer false → Banner nie gerendert →
 * der gesamte Mock-Branch in `useAuthOrSession` ist tot und tree-shaked.
 */
function DevAuthBanner() {
  return (
    <div
      role="status"
      className="mb-8 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 font-sans text-xs font-medium uppercase tracking-widest text-amber-800"
    >
      {de.login.devBanner}
    </div>
  );
}
